/*
 * bd-sample-shipping-fee.js
 *
 * The enforcement half of snippets/bd-sample-shipping-fee.liquid — see that
 * file for the rule, for which carts owe the $1 charge, and for why the charge
 * has to be a line item rather than a shipping rate on this store.
 *
 * Two jobs, in this order:
 *
 *   1. Remember that this visitor came from Meta, and put that on the cart.
 *      Liquid cannot read a cookie, so the cart attribute `meta_source` is how
 *      the rule learns where the shopper came from — and it reaches the order
 *      afterwards, which is how anyone will be able to tell whether this
 *      actually cut the spam. Same trick, same reason, as the campaign context
 *      in assets/lp-sample-picker.js.
 *
 *   2. Carry out whatever the snippet decided. It renders a marker inside
 *      <cart-items> saying 'required', 'present', 'excess', 'stale' or 'idle';
 *      this file adds, corrects or removes the line and never decides anything
 *      for itself. Nothing here re-derives "is this a sample", "is this cart
 *      free" or "did they come from Meta" — one rule, one file, the same
 *      contract snippets/bd-cart-mode.liquid and snippets/bd-sample-gate.liquid
 *      are held to.
 *
 * This is a real asset rather than an inline <script> in the snippet for the
 * reason assets/bd-sample-gate.js gives: the marker renders inside
 * <cart-items>, a region the drawer and the cart page re-render over AJAX, and
 * a <script> arriving through innerHTML never executes. Loaded from
 * layout/theme.liquid so the observer is already running when the first sample
 * lands.
 *
 * LAST TOUCH, NOT FIRST
 * The gclid block at the bottom of layout/theme.liquid is deliberately
 * first-touch: it is claiming credit for a sale, and the first ad click earns
 * that. This is the opposite problem. Charging someone $1 because they clicked
 * an Instagram ad three months ago and came back today through email would be
 * wrong, so a Meta landing refreshes the window and nothing else extends it.
 *
 * WHAT THIS CANNOT DO
 * Express checkout buttons — Shop Pay, PayPal, Apple Pay — go straight from the
 * drawer to payment without a click this file can vet. The page-load path
 * covers the ordinary cases (the fee is already on the cart by the time those
 * buttons are visible), but a shopper who adds their last sample and hits Shop
 * Pay inside the same render can still get through free. Closing that properly
 * needs a Cart Transform function, which is an app extension and does not live
 * in this repository. See docs/sample-shipping-fee-2026-08.md.
 */
(function () {
  if (window.__bdSampleShippingFeeInit) return;
  window.__bdSampleShippingFeeInit = true;

  var CONFIG = window.bdSampleFee || {};
  var COOKIE = 'bd_meta_src';
  var ATTRIBUTE = 'meta_source';
  var RELOAD_GUARD = 'bd_meta_fee_reloads';
  var MAX_RELOADS = 2;

  var WINDOW_DAYS = Number(CONFIG.windowDays) > 0 ? Number(CONFIG.windowDays) : 30;

  var routes = (window.theme && window.theme.routes) || {};
  var CART_GET = (routes.cart || '/cart') + '.js';
  var CART_ADD = (routes.cartAdd || '/cart/add') + '.js';
  var CART_CHANGE = (routes.cartChange || '/cart/change') + '.js';
  var CART_UPDATE = (routes.cartUpdate || '/cart/update') + '.js';

  /* ------------------------------------------------------------------ cookies
     Same helpers as the gclid block in layout/theme.liquid, kept local rather
     than shared because that block is inline in the layout and there is nothing
     to import from.                                                          */

  function getCookie(name) {
    var match = document.cookie.match('(?:^|;\\s*)' + name + '=([^;]+)');
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date();
    expires.setTime(expires.getTime() + days * 864e5);
    document.cookie =
      name + '=' + encodeURIComponent(value) + ';expires=' + expires.toUTCString() + ';path=/;SameSite=Lax';
  }

  /* ------------------------------------------------------- is this from Meta?
     Three signals, cheapest first. The token that comes back is stored and
     reported as-is, so an order can say which of them fired.

     utm_source is matched loosely on purpose. Meta's own {{site_source_name}}
     macro expands to bare "fb" / "ig" / "an" / "msg", campaign managers write
     "facebook" and "Instagram", and ad platforms hand through placement names
     like "Facebook_Mobile_Feed" — an exact allowlist would miss most of the
     traffic it is supposed to catch. "meta" is matched exactly and never as a
     substring, because "metal" and "metallic" are words this catalogue uses. */

  var UTM_EXACT = [
    'fb',
    'ig',
    'an',
    'msg',
    'meta',
    'messenger',
    'audience_network',
    'facebook',
    'instagram',
    'threads'
  ];
  var UTM_CONTAINS = ['facebook', 'instagram'];
  var META_HOSTS = [
    'facebook.com',
    'instagram.com',
    'messenger.com',
    'threads.net',
    'threads.com',
    'fb.com',
    'fb.me',
    'm.me'
  ];

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  function detectMetaTouch() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }

    // A click id is unambiguous: only Meta sets it.
    if (params.get('fbclid')) return 'fbclid';

    var source = normalise(params.get('utm_source'));
    if (source) {
      if (UTM_EXACT.indexOf(source) > -1) return 'utm:' + source;
      for (var i = 0; i < UTM_CONTAINS.length; i++) {
        if (source.indexOf(UTM_CONTAINS[i]) > -1) return 'utm:' + source;
      }
    }

    // Organic Meta counts too. The spam does not only arrive through paid ads —
    // a free-sample link shared into a Facebook group behaves the same way.
    var referrer = document.referrer;
    if (referrer) {
      var host;
      try {
        host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
      } catch (e) {
        host = '';
      }
      if (host && host !== window.location.hostname) {
        for (var j = 0; j < META_HOSTS.length; j++) {
          var candidate = META_HOSTS[j];
          if (host === candidate || host.slice(-(candidate.length + 1)) === '.' + candidate) {
            return 'ref:' + candidate;
          }
        }
      }
    }

    return null;
  }

  /**
   * The Meta token for this visitor, refreshing the window on a fresh touch.
   * @returns {?string}
   */
  function metaToken() {
    var incoming = detectMetaTouch();
    if (incoming) {
      setCookie(COOKIE, incoming, WINDOW_DAYS);
      return incoming;
    }
    return getCookie(COOKIE);
  }

  /* -------------------------------------------------------------- cart writes */

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error(url + ' responded ' + response.status);
      return response.json();
    });
  }

  /**
   * Put the Meta token on the cart so Liquid can see it.
   *
   * Written on every page a Meta visitor loads rather than waiting for a cart
   * to exist, so the attribute is already there when the first sample lands and
   * the very next render of <cart-items> can decide. The alternative — write it
   * once a cart exists — costs an extra round trip on exactly the path that
   * matters, and sections/lp-sample-picker.liquid adds samples without
   * re-rendering the cart region at all, so that trip might not happen before
   * checkout.
   *
   * @param {string} token
   * @returns {Promise<boolean>} whether the cart changed
   */
  function syncAttribute(token) {
    return fetch(CART_GET)
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (cart.attributes && cart.attributes[ATTRIBUTE] === token) return false;
        var attributes = {};
        attributes[ATTRIBUTE] = token;
        return postJSON(CART_UPDATE, { attributes: attributes }).then(function () {
          return true;
        });
      })
      .catch(function () {
        return false;
      });
  }

  /* ------------------------------------------------------------- re-rendering
     After a write, the cart region has to be re-rendered so the snippet can
     re-decide — otherwise the marker still says 'required' and the observer
     would ask for the line a second time. The drawer has an event for it. The
     cart page does not, so it reloads, which is what
     assets/bd-sample-gate.js does on the same problem. The reload is counted
     because a state that never resolves must not become a refresh loop.      */

  function reRender() {
    var onCartPage = !!document.querySelector('.cc-main-cart');
    if (!onCartPage) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      return;
    }

    var count = 0;
    try {
      count = Number(sessionStorage.getItem(RELOAD_GUARD)) || 0;
    } catch (e) {
      count = MAX_RELOADS; // no sessionStorage, no way to count — do not risk it
    }
    if (count >= MAX_RELOADS) return;
    try {
      sessionStorage.setItem(RELOAD_GUARD, String(count + 1));
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  }

  /* ------------------------------------------------- carrying out the decision */

  var busy = false;

  /**
   * Act on one marker. The marker is the snippet's answer; this only obeys it.
   * @param {Element} marker
   * @returns {Promise<boolean>} whether the cart changed
   */
  function applyState(marker) {
    var state = marker.getAttribute('data-bd-sample-fee-state');
    var variantId = marker.getAttribute('data-bd-sample-fee-variant');
    var key = marker.getAttribute('data-bd-sample-fee-key');

    if (state === 'required' && variantId) {
      // A line item property rather than a cart attribute, because this one
      // belongs to the line: it is why this $1 is on the order at all, and it
      // survives into the order's line detail for NetSuite. The leading
      // underscore keeps it out of the shopper's cart and checkout display.
      return postJSON(CART_ADD, {
        items: [{ id: Number(variantId), quantity: 1, properties: { _sample_shipping: 'meta' } }]
      }).then(function () {
        return true;
      });
    }

    if (state === 'excess' && key) {
      return postJSON(CART_CHANGE, { id: key, quantity: 1 }).then(function () {
        return true;
      });
    }

    if (state === 'stale' && key) {
      return postJSON(CART_CHANGE, { id: key, quantity: 0 }).then(function () {
        return true;
      });
    }

    return Promise.resolve(false);
  }

  /**
   * Mark every marker currently in the DOM as acted upon.
   *
   * Two things make this necessary, and both of them add the fee twice without
   * it. The cart page carries a marker of its own *and* the drawer's, rendered
   * from the same cart — so a single decision is on the page twice. And a
   * decision that could not be re-rendered (no drawer on the page to refresh,
   * a reload the guard refused) leaves a marker still reading 'required' while
   * the fee is already on the cart, which the observer would obey again on the
   * next DOM change, and the change after that.
   *
   * A marker is the answer to one render. Once obeyed it is spent; the next
   * render produces a fresh one.
   */
  function markHandled() {
    document.querySelectorAll('[data-bd-sample-fee]').forEach(function (el) {
      el.dataset.bdSampleFeeHandled = '1';
    });
  }

  /**
   * Find the marker on the page and act on it.
   * @returns {Promise<boolean>} whether the cart changed
   */
  function settle() {
    if (busy) return Promise.resolve(false);

    var marker = document.querySelector('[data-bd-sample-fee]:not([data-bd-sample-fee-handled="1"])');
    if (!marker) return Promise.resolve(false);

    var state = marker.getAttribute('data-bd-sample-fee-state');
    if (state === 'idle' || state === 'present') {
      markHandled();
      return Promise.resolve(false);
    }

    busy = true;
    markHandled();
    return applyState(marker)
      .then(function (changed) {
        busy = false;
        if (changed) reRender();
        return changed;
      })
      .catch(function () {
        // Fail open. A sample that ships without its $1 is a cheaper mistake
        // than a cart that cannot be paid for, and the marker will be
        // re-evaluated on the next render anyway.
        busy = false;
        return false;
      });
  }

  /* ------------------------------------------------------- the checkout boundary
     Not every path re-renders the cart region. assets/lp-sample-picker.js —
     the picker on the campaign landing pages, which is where most Meta traffic
     lands — writes to the cart and refreshes only its own tray, so the marker
     rendered with the page can still say 'idle' while the cart now holds five
     samples. Rather than teach this file the rule so it can decide for itself,
     it asks Liquid again: the Section Rendering API re-renders the cart region
     against the live cart, and the marker in that HTML is the current answer.

     Everything here fails open and on a timer. A shopper must never be stranded
     at a dead checkout button over $1.                                       */

  function cartSectionId() {
    var region = document.querySelector('cart-items');
    var section = region ? region.closest('.shopify-section') : null;
    return section ? section.id.replace(/^shopify-section-/, '') : null;
  }

  function freshMarker() {
    var id = cartSectionId();
    if (!id) return Promise.resolve(null);

    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(id))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var html = data[id];
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bd-sample-fee]');
      })
      .catch(function () {
        return null;
      });
  }

  function vetCheckout() {
    return freshMarker().then(function (marker) {
      if (!marker) return false;
      var state = marker.getAttribute('data-bd-sample-fee-state');
      if (state === 'idle' || state === 'present') return false;
      busy = true;
      // The marker in the DOM is now out of date whatever happens next, and it
      // must not be obeyed a second time on the way out.
      markHandled();
      return applyState(marker)
        .then(function (changed) {
          busy = false;
          return changed;
        })
        .catch(function () {
          busy = false;
          return false;
        });
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var done = function (value) {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      setTimeout(function () {
        done(false);
      }, ms);
      promise.then(done, function () {
        done(false);
      });
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!trigger) return;
      // Already vetted: this is the synthetic click that continues the journey.
      if (trigger.dataset.bdSampleFeeVetted === '1') return;
      if (!getCookie(COOKIE)) return;

      event.preventDefault();
      trigger.dataset.bdSampleFeeVetted = '1';

      withTimeout(vetCheckout(), 2500).then(function () {
        trigger.click();
      });
    },
    true
  );

  /* ----------------------------------------------------------------- start up */

  var token = metaToken();

  if (token) {
    syncAttribute(token).then(function (changed) {
      // A cart that just learned where it came from has to be re-rendered
      // before the snippet can act on it — but only when there is something to
      // decide about. An empty cart owes nothing.
      if (changed && document.querySelector('cart-items:not([data-empty="true"])')) {
        reRender();
        return;
      }
      settle();
    });
  } else {
    // No Meta token and the cart still carries the fee — a shopper whose window
    // has expired, or who cleared the cookie. The snippet says 'stale' and the
    // line comes off.
    settle();
  }

  /*
   * The marker arrives over AJAX on the most common path there is — adding a
   * sample from a collection card — so the observer has to already be running
   * by then. Debounced to one check per frame, and settle() is a no-op unless
   * the marker actually asks for something.
   */
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      settle();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
